import React, { useState } from 'react';
import { Avatar, Box, Collapse, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { OPENREMIT_LOGO_ALT, OPENREMIT_LOGO_SRC } from '../utils/brand';
interface SidebarProps {
  open: boolean;
  onClose: () => void;
  activePage?: string;
}
type MenuItem = {
  text: string;
  icon: string;
  path: string;
  id: string;
  roles: string[];
};
type MenuGroup = {
  text: string;
  icon: string;
  activeIds: string[];
  items: Omit<MenuItem, 'roles'>[];
};
const drawerWidth = 240;
const OpenRemitLogo = () => <Box component="img" src={OPENREMIT_LOGO_SRC} alt={OPENREMIT_LOGO_ALT} sx={{
  width: '100%',
  maxWidth: 170,
  height: 'auto',
  display: 'block'
}} data-id="element-2229" data-mp="3gbx1p794xs6q" />;
const Sidebar: React.FC<SidebarProps> = ({
  open,
  onClose,
  activePage = 'dashboard'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const userRole = localStorage.getItem('userRole') || 'maker';
  const isChecker = userRole === 'checker';
  const isDarkMode = theme.palette.mode === 'dark';
  const branchActiveIds = ['branch-users', 'user-management', 'bulk-upload', 'bulk-upload-lists', 'file-upload', 'maker-inbox', 'checker-inbox'];
  const partnersActiveIds = ['partners', 'partner-users'];
  const subAgentsActiveIds = ['sub-agents', 'sub-agent-users'];
  const [branchUsersOpen, setBranchUsersOpen] = useState(branchActiveIds.includes(activePage));
  const [partnersOpen, setPartnersOpen] = useState(partnersActiveIds.includes(activePage));
  const [subAgentsOpen, setSubAgentsOpen] = useState(subAgentsActiveIds.includes(activePage));
  const sidebarSurface = isDarkMode ? '#13112B' : '#FFFFFF';
  const sidebarRaisedSurface = isDarkMode ? 'linear-gradient(135deg, rgba(91,78,240,0.18) 0%, rgba(0,180,179,0.10) 100%)' : 'linear-gradient(135deg, rgba(91,78,240,0.10) 0%, rgba(0,180,179,0.09) 100%)';
  const menuItems: MenuItem[] = [{
    text: 'Dashboard',
    icon: 'lucide:layout-dashboard',
    path: '/',
    id: 'dashboard',
    roles: ['maker', 'checker']
  }, {
    text: 'Transactions',
    icon: 'lucide:banknote',
    path: '/transactions',
    id: 'transactions',
    roles: ['maker', 'checker']
  }, {
    text: 'Failed Transactions',
    icon: 'lucide:folder-kanban',
    path: '/failed-tabs',
    id: 'failed-tabs',
    roles: ['maker']
  }, {
    text: 'RTGS Transactions',
    icon: 'lucide:building-2',
    path: '/rtgs',
    id: 'rtgs',
    roles: ['maker']
  }, {
    text: 'Checker Inbox',
    icon: 'lucide:check-square',
    path: '/checker-approvals',
    id: 'checker-approvals',
    roles: ['maker', 'checker']
  }, {
    text: 'Maker Inbox',
    icon: 'lucide:inbox',
    path: '/maker-approvals',
    id: 'maker-approvals',
    roles: ['maker']
  }, {
    text: 'Checker - Manual Settlement',
    icon: 'lucide:stamp',
    path: '/checker-manual-settlement',
    id: 'checker-manual-settlement',
    roles: ['maker', 'checker']
  }, {
    text: 'Maker - Manual Settlement',
    icon: 'lucide:inbox',
    path: '/maker-manual-settlement',
    id: 'maker-manual-settlement',
    roles: ['maker']
  }, {
    text: 'Transaction Reversal',
    icon: 'lucide:undo-2',
    path: '/transaction-reversal',
    id: 'transaction-reversal',
    roles: ['maker']
  }, {
    text: 'Reversal Lists',
    icon: 'lucide:list',
    path: '/reversal-lists',
    id: 'reversal-lists',
    roles: ['maker']
  }, {
    text: 'e-PRC Generation',
    icon: 'lucide:file-text',
    path: '/eprc-generation',
    id: 'eprc-generation',
    roles: ['maker']
  }, {
    text: 'Compliance Review',
    icon: 'lucide:shield-check',
    path: '/compliance',
    id: 'compliance',
    roles: ['maker']
  }, {
    text: 'Alert Configuration',
    icon: 'lucide:bell-ring',
    path: '/alert-configuration',
    id: 'alert-configuration',
    roles: ['maker']
  }, {
    text: 'Nationality Block',
    icon: 'lucide:flag',
    path: '/nationality-block',
    id: 'nationality-block',
    roles: ['maker', 'checker']
  }, {
    text: 'Purpose of Payment Block',
    icon: 'lucide:file-warning',
    path: '/purpose-payment-block',
    id: 'purpose-payment-block',
    roles: ['maker', 'checker']
  }, {
    text: 'Txn Type Config',
    icon: 'lucide:sliders-horizontal',
    path: '/partner-txn-config',
    id: 'partner-txn-config',
    roles: ['maker']
  }, {
    text: 'Business Keywords',
    icon: 'lucide:list-filter',
    path: '/business-keyword-config',
    id: 'business-keyword-config',
    roles: ['maker']
  }, {
    text: 'Holiday Calendar',
    icon: 'lucide:calendar-days',
    path: '/holiday-calendar',
    id: 'holiday-calendar',
    roles: ['maker']
  }].filter(item => item.roles.includes(userRole));
  const groups: MenuGroup[] = [{
    text: 'Branch Records',
    icon: 'lucide:users',
    activeIds: branchActiveIds,
    items: [{
      text: 'User Management',
      icon: 'lucide:user-cog',
      path: '/branch-users/user-management',
      id: 'user-management'
    }, {
      text: 'Bulk Upload',
      icon: 'lucide:upload',
      path: '/branch-users/bulk-upload',
      id: 'bulk-upload'
    }, {
      text: 'File Upload',
      icon: 'lucide:file-up',
      path: '/branch-users/file-upload',
      id: 'file-upload'
    }, {
      text: 'Bulk Upload Lists',
      icon: 'lucide:file-text',
      path: '/branch-users/bulk-upload/lists',
      id: 'bulk-upload-lists'
    }, {
      text: 'Maker Inbox',
      icon: 'lucide:inbox',
      path: '/branch-users/maker-inbox',
      id: 'maker-inbox'
    }, {
      text: 'Checker Inbox',
      icon: 'lucide:check-square',
      path: '/branch-users/checker-inbox',
      id: 'checker-inbox'
    }]
  }, {
    text: 'Partners',
    icon: 'lucide:handshake',
    activeIds: partnersActiveIds,
    items: [{
      text: 'Partner Management',
      icon: 'lucide:briefcase',
      path: '/partners',
      id: 'partners'
    }, {
      text: 'User Management',
      icon: 'lucide:user-cog',
      path: '/partner-users',
      id: 'partner-users'
    }]
  }, {
    text: 'Sub Agents',
    icon: 'lucide:network',
    activeIds: subAgentsActiveIds,
    items: [{
      text: 'Sub Agents Management',
      icon: 'lucide:clipboard-list',
      path: '/sub-agents',
      id: 'sub-agents'
    }, {
      text: 'User Management',
      icon: 'lucide:user-circle',
      path: '/sub-agent-users',
      id: 'sub-agent-users'
    }]
  }];
  const go = (path: string) => {
    if (isMobile) onClose();
    navigate(path);
  };
  const itemSx = (active: boolean, nested = false): SxProps<Theme> => ({
    borderRadius: 2,
    py: nested ? 1 : 1.2,
    pl: nested ? 4 : undefined,
    backgroundColor: active ? 'rgba(79, 70, 229, 0.11)' : 'transparent',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 180ms ease',
    '&:hover': {
      backgroundColor: active ? 'rgba(79, 70, 229, 0.15)' : 'rgba(79, 70, 229, 0.06)',
      transform: 'translateX(3px)'
    },
    '&::before': active ? {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      width: 3,
      height: '60%',
      background: 'linear-gradient(180deg, #5B4EF0 0%, #00B4B3 100%)',
      borderRadius: '0 4px 4px 0',
      boxShadow: '0 0 8px rgba(79,70,229,0.5)'
    } : {}
  });
  const iconSx = (active: boolean, nested = false): SxProps<Theme> => ({
    minWidth: nested ? 28 : 36,
    color: active ? '#5B4EF0' : theme.palette.text.secondary
  });
  const textSx = (active: boolean, nested = false): SxProps<Theme> => ({
    '& .MuiTypography-root': {
      fontWeight: active ? 700 : 600,
      color: active ? '#5B4EF0' : theme.palette.text.primary,
      fontSize: nested ? '0.85rem' : '0.9rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  });
  const renderItem = (item: Omit<MenuItem, 'roles'>, nested = false) => {
    const active = item.id === activePage;
    return <ListItem key={item.id} disablePadding sx={{
      mb: 0.5
    }} data-id="element-2230" data-mp="a76ghj1lzghp">
        <Tooltip title={item.text} placement="right" arrow data-id="element-2231" data-mp="a9k7ngt648jz">
          <ListItemButton sx={itemSx(active, nested)} onClick={() => go(item.path)} data-id="element-2232" data-mp="59elgg5o3e9a">
            <ListItemIcon sx={iconSx(active, nested)} data-id="element-2233" data-mp="j8eahox3dv5z"><Icon icon={item.icon} width={nested ? 16 : 20} height={nested ? 16 : 20} data-id="element-2234" data-mp="j9foo29emxrx" /></ListItemIcon>
            <ListItemText primary={item.text} sx={textSx(active, nested)} data-id="element-2235" data-mp="e7qc2i6jac1q" />
          </ListItemButton>
        </Tooltip>
      </ListItem>;
  };
  const renderGroup = (group: MenuGroup, isOpen: boolean, setOpen: (v: boolean) => void) => {
    const active = group.activeIds.includes(activePage);
    return <React.Fragment key={group.text} data-id="element-2236" data-mp="1y7lalqak3kjx">
        <ListItem disablePadding sx={{
        mb: 0.5
      }} data-id="element-2237" data-mp="2272ewge8ybni">
          <ListItemButton sx={itemSx(active)} onClick={() => setOpen(!isOpen)} data-id="element-2238" data-mp="224pp5h28ywzw">
            <ListItemIcon sx={iconSx(active)} data-id="element-2239" data-mp="1o8m1y7psjowx"><Icon icon={group.icon} width={20} height={20} data-id="element-2240" data-mp="1t64ahwzwc24e" /></ListItemIcon>
            <ListItemText primary={group.text} sx={textSx(active)} data-id="element-2241" data-mp="1t88bo76eun0z" />
            <Icon icon={isOpen ? 'lucide:chevron-up' : 'lucide:chevron-down'} width={18} height={18} color={theme.palette.text.secondary} data-id="element-2242" data-mp="2gx1bieq5lf0v" />
          </ListItemButton>
        </ListItem>
        <Collapse in={isOpen} timeout="auto" unmountOnExit data-id="element-2243" data-mp="276orqoq5xpvs">
          <List component="div" disablePadding sx={{
          ml: 1,
          mb: 0.5
        }} data-id="element-2244" data-mp="2742k6p39vadk">{group.items.map(item => renderItem(item, true))}</List>
        </Collapse>
      </React.Fragment>;
  };
  const drawerContent = <Box sx={{
    width: drawerWidth,
    height: '100%',
    bgcolor: sidebarSurface,
    color: 'text.primary',
    borderRight: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      width: 3,
      background: 'linear-gradient(180deg, #5B4EF0 0%, #00B4B3 100%)'
    }
  }} data-id="element-2245" data-mp="34las928ifcl5">
      <Box sx={{
      display: 'flex',
      alignItems: 'center',
      px: 2,
      py: 1,
      minHeight: 64,
      borderBottom: `1px solid ${theme.palette.divider}`
    }} data-id="element-2246" data-mp="3i9aeq5prepo3">
        <OpenRemitLogo data-id="element-2247" data-mp="38gwg3ltz9z65" />
      </Box>
      <Box sx={{
      px: 2,
      my: 1.5
    }} data-id="element-2248" data-mp="38ftofbotzsp6">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        p: 1.5,
        borderRadius: 2,
        background: sidebarRaisedSurface,
        border: '1px solid rgba(91,78,240,0.18)',
        boxShadow: '0 8px 18px rgba(34,32,88,0.08)'
      }} data-id="element-2249" data-mp="3d7l4ute9wi1l">
          <Avatar sx={{
          width: 38,
          height: 38,
          bgcolor: 'background.paper',
          color: isChecker ? '#10B981' : 'primary.main',
          fontWeight: 700,
          border: '2px solid',
          borderColor: isChecker ? '#10B981' : 'primary.main'
        }} data-id="element-2250" data-mp="33hjqitbq2dzv">{isChecker ? 'C' : 'A'}</Avatar>
          <Box sx={{
          ml: 1.5,
          minWidth: 0
        }} data-id="element-2251" data-mp="2ugllc7pjs0fp">
            <Typography variant="subtitle2" sx={{
            fontWeight: 700,
            lineHeight: 1.2
          }} data-id="element-2252" data-mp="2avbpihj5znq5">{isChecker ? 'Checker User' : 'Admin User'}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{
            fontSize: '0.7rem'
          }} data-id="element-2253" data-mp="25y1n81aofhkw">{isChecker ? 'Amendment Checker' : 'Super Admin'}</Typography>
          </Box>
        </Box>
      </Box>
      <List sx={{
      px: 1.5,
      flex: 1,
      overflow: 'auto',
      pb: 2
    }} data-id="element-2254" data-mp="2ftvbghxrczii">
        <Typography sx={{
        px: 1,
        pt: 1,
        pb: 0.75,
        color: 'text.secondary',
        fontSize: '0.68rem',
        fontWeight: 800
      }} data-id="element-2255" data-mp="2ttxmplbk8uos">OPERATIONS</Typography>
        {menuItems.map(item => renderItem(item))}
        {!isChecker && renderGroup(groups[0], branchUsersOpen, setBranchUsersOpen)}
        {!isChecker && renderGroup(groups[1], partnersOpen, setPartnersOpen)}
        {!isChecker && renderGroup(groups[2], subAgentsOpen, setSubAgentsOpen)}
      </List>
    </Box>;
  return isMobile ? <Drawer anchor="left" open={open} onClose={onClose} sx={{
    '& .MuiDrawer-paper': {
      width: drawerWidth,
      boxSizing: 'border-box',
      overflow: 'hidden',
      bgcolor: sidebarSurface,
      color: 'text.primary'
    }
  }} data-id="element-2256" data-mp="3cikpfx69l7z7">{drawerContent}</Drawer> : <Drawer variant="permanent" sx={{
    width: drawerWidth,
    flexShrink: 0,
    '& .MuiDrawer-paper': {
      width: drawerWidth,
      boxSizing: 'border-box',
      overflow: 'hidden',
      bgcolor: sidebarSurface,
      color: 'text.primary',
      borderRight: `1px solid ${theme.palette.divider}`
    }
  }} data-id="element-2257" data-mp="3kl3iztskyy6s">{drawerContent}</Drawer>;
};
export default Sidebar;